/**
 * Admin Users React Query Hooks (Task 13 — internal user management)
 *
 * Listing/search and every mutation (set role, ban/unban, revoke sessions,
 * delete) go through Better Auth's admin client (`authClient.admin.*`),
 * which is the real authorization boundary — its endpoints enforce
 * `adminRoles` server-side regardless of what this UI does (see
 * apps/api/src/auth.ts's `admin({ adminRoles: ["admin"] })`). This file
 * only wraps those calls for TanStack Query caching/invalidation; it never
 * re-implements the authorization decision.
 *
 * Provider badges and last-login are the one gap Better Auth's admin
 * surface can't fill (see lib/api/admin-users.ts for why) and are fetched
 * from this app's own `/admin/users/enrichment` endpoint instead.
 */
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { authClient } from "../auth-client";
import {
  fetchAdminRetailerApiUsage,
  fetchAdminUserEnrichment,
} from "../api/admin-users";

export const ADMIN_USERS_QUERY_KEY = "admin-users";
export const ADMIN_RETAILER_API_USAGE_QUERY_KEY = "admin-retailer-api-usage";

/**
 * Matches the installed better-auth 1.5.6 admin client's inferred role
 * literal union (see `authClient.admin.setRole`'s generated type in
 * node_modules — the client's default `roles: { admin: Role; user: Role }`
 * restricts this to a literal union, not a generic `string`, since
 * apps/web/lib/auth-client.ts registers `adminClient()` without custom
 * roles). Mirrors apps/api/src/auth.ts's `admin({ adminRoles: ["admin"] })`.
 */
export type AdminRole = "user" | "admin" | "moderator";

/**
 * "moderator" (recipe staff, owner decision 2026-09-16) is not in the client's
 * inferred literal union above. The server's set-role endpoint only rejects
 * unknown roles when custom `roles` are configured, and apps/api/src/auth.ts
 * configures none (better-auth 1.5.6 dist/plugins/admin/routes.mjs, set-role),
 * so the value is stored as-is. The cast is confined to this one call.
 */
type ClientSetRoleInput = Parameters<typeof authClient.admin.setRole>[0];

/**
 * Better Auth client methods resolve to `{ data, error }` rather than
 * throwing (see the identical helper in use-user.ts). Every mutation below
 * unwraps that shape so TanStack Query's `isError`/`onError` and this
 * component's try/catch both see a rejected promise on failure.
 */
function unwrapAuthResult<T>(result: {
  data: T | null;
  error: { message?: string } | null;
}): T {
  if (result.error) {
    throw new Error(
      result.error.message || "Something went wrong. Please try again.",
    );
  }
  return result.data as T;
}

export interface AdminUsersListParams {
  search?: string;
  page: number;
  limit: number;
}

/** List/search users via Better Auth's admin plugin (server-paginated). */
export function useAdminUsersList({
  search,
  page,
  limit,
}: AdminUsersListParams) {
  const offset = (page - 1) * limit;

  return useQuery({
    queryKey: [ADMIN_USERS_QUERY_KEY, "list", { search, page, limit }],
    queryFn: () =>
      authClient.admin
        .listUsers({
          query: {
            limit,
            offset,
            sortBy: "createdAt",
            sortDirection: "desc",
            ...(search
              ? {
                  searchField: "email" as const,
                  searchOperator: "contains" as const,
                  searchValue: search,
                }
              : {}),
          },
        })
        .then(unwrapAuthResult),
    // Keeps the current page's rows on screen while the next page loads
    // (pagination/search) instead of flashing the loading spinner every
    // time `page` or `search` changes.
    placeholderData: keepPreviousData,
  });
}

/** Batched provider/last-login lookup for the currently visible page of users. */
export function useAdminUserEnrichment(userIds: string[]) {
  return useQuery({
    queryKey: [ADMIN_USERS_QUERY_KEY, "enrichment", userIds],
    queryFn: () => fetchAdminUserEnrichment(userIds),
    enabled: userIds.length > 0,
  });
}

export function useAdminRetailerApiUsage() {
  return useQuery({
    queryKey: [ADMIN_RETAILER_API_USAGE_QUERY_KEY],
    queryFn: fetchAdminRetailerApiUsage,
  });
}

function useInvalidateAdminUsers() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: [ADMIN_USERS_QUERY_KEY] });
}

export function useSetUserRole() {
  const invalidate = useInvalidateAdminUsers();

  return useMutation({
    mutationFn: (input: { userId: string; role: AdminRole }) =>
      authClient.admin
        .setRole(input as unknown as ClientSetRoleInput)
        .then(unwrapAuthResult),
    onSuccess: invalidate,
  });
}

export function useBanUser() {
  const invalidate = useInvalidateAdminUsers();

  return useMutation({
    mutationFn: (input: { userId: string; banReason?: string }) =>
      authClient.admin.banUser(input).then(unwrapAuthResult),
    onSuccess: invalidate,
  });
}

export function useUnbanUser() {
  const invalidate = useInvalidateAdminUsers();

  return useMutation({
    mutationFn: (input: { userId: string }) =>
      authClient.admin.unbanUser(input).then(unwrapAuthResult),
    onSuccess: invalidate,
  });
}

/**
 * Revokes ALL of a user's sessions (not a single session) — matches the
 * dense admin-table UX (one action per row, not a per-session drill-down).
 */
export function useRevokeUserSessions() {
  const invalidate = useInvalidateAdminUsers();

  return useMutation({
    mutationFn: (input: { userId: string }) =>
      authClient.admin.revokeUserSessions(input).then(unwrapAuthResult),
    onSuccess: invalidate,
  });
}

/**
 * Permanently deletes a user (Better Auth's `/admin/remove-user`). Better
 * Auth itself refuses this for the caller's own id (400
 * YOU_CANNOT_REMOVE_YOURSELF) — the UI's self-lockout guard in
 * AdminUsers.tsx is a courtesy, not the enforcement boundary.
 */
export function useRemoveUser() {
  const invalidate = useInvalidateAdminUsers();

  return useMutation({
    mutationFn: (input: { userId: string }) =>
      authClient.admin.removeUser(input).then(unwrapAuthResult),
    onSuccess: invalidate,
  });
}
